/**
 * @fileoverview Tab ID Singleton
 * @description Ensures a single, consistent Tab ID is used per browser tab.
 * It attaches the ID to the `window` object to survive module reloads (e.g., HMR).
 */
declare global {
  interface Window {
    __authTabId?: string;
  }
}

/**
 * @description Generates or retrieves a unique tab identifier for the current browser tab.
 * This function ensures a single, consistent Tab ID is used per browser tab by storing
 * it on the window object to survive module reloads (e.g., HMR). Returns null when
 * running in server-side environment.
 *
 * @returns {string | null} A unique 7-character alphanumeric string for the current tab,
 * or null if running in a server-side environment (window is undefined)
 *
 * @example
 * ```typescript
 * const tabId = getTabId();
 * if (tabId) {
 *   console.log(`Current tab ID: ${tabId}`);
 * }
 * ```
 */
export const getTabId = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!window.__authTabId) {
    window.__authTabId = Math.random().toString(36).substring(2, 9);
  }
  return window.__authTabId;
};
